import React, { useRef, useState, VoidFunctionComponent } from 'react';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useMultiElementIsFocused } from '../../hooks/useMultiElementIsFocused/useMultiElementIsFocused';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { DateInput } from '../DateInput/DateInput';
import {
  InputLayout,
  InputLayoutProps
} from '../../input/InputLayout/InputLayout';
import { TimeInput, TimeInputRef } from '../TimeInput/TimeInput';
import styles from './DateTimeInput.module.css';

export type DateTimeInputProps = PropsWithoutChildren<
  Omit<
    InputLayoutProps,
    'onInput' | 'onChange' | 'onInputCapture' | 'onChangeCapture' | 'multiline'
  >
> & {
  value: Date | null;
  onInput: (value: Date | null) => void;
};

export const DateTimeInput: VoidFunctionComponent<DateTimeInputProps> =
  React.memo((props) => {
    const { value, onInput, ...remainingProps } = prepareProps({
      props,
      ignoreChildren: true
    });
    const timeInputRef = useRef<TimeInputRef | null>(null);
    const [bufferedValue, setBufferedValue] = useState(value);

    const { isFocused, focusHandlerFactory, blurHandlerFactory } =
      useMultiElementIsFocused({
        onFocus: () => {
          setBufferedValue(value);
        },
        onBlur: () => {
          if (bufferedValue?.getTime() !== value?.getTime()) {
            onInput(bufferedValue);
          }
        }
      });

    const acceptNewValue = useEventCallback((newValue: Date | null) => {
      if (isFocused) {
        setBufferedValue(newValue);
      } else {
        onInput(newValue);
      }
    });

    const handleDateInput = acceptNewValue;

    const handleDateInputFinished = useEventCallback(() => {
      timeInputRef.current?.focus();
    });

    const handleTimeInput = useEventCallback(
      (hours: number | null, minutes: number | null) => {
        // should never happen since it is disabled when there is no value
        assertNotNullOrUndefined(bufferedValue, 'no value available');

        let newValue = null;
        if (hours != null && minutes != null) {
          newValue = new Date(bufferedValue);
          newValue.setHours(hours);
          newValue.setMinutes(minutes);
        }

        acceptNewValue(newValue);
      }
    );

    const displayedValue = isFocused ? bufferedValue : value;

    return (
      <InputLayout
        {...remainingProps}
        multiline={true}
      >
        {() => (
          <div className={styles.inputsWrapper}>
            <DateInput
              label=""
              value={displayedValue}
              onInput={handleDateInput}
              onFocus={focusHandlerFactory('DateInput')}
              onBlur={blurHandlerFactory('DateInput')}
              onInputFinished={handleDateInputFinished}
              data-testid="DateTimeInput.DateInput"
            ></DateInput>
            <TimeInput
              ref={timeInputRef}
              label=""
              hours={displayedValue?.getHours() ?? null}
              minutes={displayedValue?.getMinutes() ?? null}
              onInput={handleTimeInput}
              onFocus={focusHandlerFactory('TimeInput')}
              onBlur={blurHandlerFactory('TimeInput')}
              readOnly={!displayedValue}
              data-testid="DateTimeInput.TimeInput"
            ></TimeInput>
          </div>
        )}
      </InputLayout>
    );
  });
